/* global process */
import React, { useEffect, useRef, useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { Box, Typography, Drawer, CircularProgress } from "@mui/material";
import { AlertTriangle, Maximize2, Minimize2 } from "lucide-react";

let MAPBOX_TOKEN;
try {
  MAPBOX_TOKEN = process.env.MAPBOX_ACCESS_TOKEN;
} catch {
  MAPBOX_TOKEN = undefined;
}
const DEFAULT_CENTER = { lat: 50.5039, lng: 4.4699 };
const CLUSTER_RADIUS_PX = 52;
const LABEL_HEIGHT_PX = 24;
const LABEL_GAP_PX = 0;
const LABEL_MAX_WIDTH_PX = 170;
const ZOOM_BUTTON_STEP = 0.5;

let measureContext = null;
const labelTextWidth = (text) => {
  if (!measureContext && typeof document !== "undefined") {
    measureContext = document.createElement("canvas").getContext("2d");
  }
  if (!measureContext) return 7 * text.length;
  measureContext.font = "12px \"Segoe UI Bold\", sans-serif";
  return measureContext.measureText(text).width;
};

const safely = (fn) => {
  try {
    fn();
  } catch (_) {
    return false;
  }
  return true;
};

const groupPoints = (points) => {
  const groups = {};
  points.forEach((point) => {
    if (point?.lat == null || point?.lng == null) return;
    const lat = Number(point.lat);
    const lng = Number(point.lng);
    const key = `${lat.toFixed(3)}_${lng.toFixed(3)}`;
    if (!groups[key]) groups[key] = { key, lat, lng, items: [] };
    if (!groups[key].items.some((item) => item.id === point.id)) groups[key].items.push(point);
  });
  return Object.values(groups);
};

/**
 * General-purpose Mapbox map: lifecycle, screen-space clustering, markers, touch
 * hit-testing and the desktop popup / mobile sheet. Domain-agnostic — adapters
 * (JobsMap, CandidatesMap) map their records to `points` ([{ id, lat, lng, data }])
 * and render rows with `renderItems`; `getLabel` (text beside the dot, shown while at most
 * `labelLimit` markers are on screen), `focus`, `loading`, the control labels
 * (`fullscreenLabel`, `exitFullscreenLabel`) and `language` (map labels,
 * e.g. "nl") are optional. Picking a row in a popup/sheet also leaves full screen.
 */
const GeoMap = ({
  points = [],
  getLabel,
  labelLimit = 8,
  renderItems,
  focus = null,
  loading = false,
  loadingMessage,
  fullscreenLabel,
  exitFullscreenLabel,
  language,
  emptyMessage,
  errorMessage,
}) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const [mapReady, setMapReady] = useState(false);
  const [error, setError] = useState(null);
  const groupsRef = useRef([]);
  const renderedRef = useRef([]);
  const clusterSignatureRef = useRef("");
  const [popupContainer, setPopupContainer] = useState(null);
  const popupRef = useRef(null);
  const [sheetGroup, setSheetGroup] = useState(null);
  const wrapperRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [canFullscreen] = useState(
    () => typeof document !== "undefined"
      && Boolean(document.fullscreenEnabled || document.webkitFullscreenEnabled),
  );

  const exitFullscreen = useCallback(() => {
    if (!(document.fullscreenElement || document.webkitFullscreenElement)) return;
    const exit = document.exitFullscreen || document.webkitExitFullscreen;
    safely(() => exit.call(document)?.catch?.(() => {}));
  }, []);

  const toggleFullscreen = useCallback(() => {
    const node = wrapperRef.current;
    if (!node) return;
    if (isFullscreen) {
      exitFullscreen();
      return;
    }
    const request = node.requestFullscreen || node.webkitRequestFullscreen;
    safely(() => request.call(node)?.catch?.(() => {}));
  }, [isFullscreen, exitFullscreen]);

  useEffect(() => {
    const node = wrapperRef.current;
    const onChange = () => {
      const current = document.fullscreenElement || document.webkitFullscreenElement;
      setIsFullscreen(Boolean(current) && current === wrapperRef.current);
    };
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
      const current = document.fullscreenElement || document.webkitFullscreenElement;
      if (node && current === node) exitFullscreen();
    };
  }, [exitFullscreen]);
  const getLabelRef = useRef(getLabel);
  const labelLimitRef = useRef(labelLimit);
  getLabelRef.current = getLabel;
  labelLimitRef.current = labelLimit;

  const [isTouchLayout] = useState(
    () => typeof window !== "undefined"
      && window.matchMedia("(max-width: 900px), (pointer: coarse)").matches,
  );

  useEffect(() => {
    if (!containerRef.current || !MAPBOX_TOKEN) {
      if (!MAPBOX_TOKEN) setError("Mapbox token missing");
      return undefined;
    }

    let cancelled = false;

    const init = async () => {
      try {
        const mapboxgl = (await import("mapbox-gl")).default;
        await import("mapbox-gl/dist/mapbox-gl.css");

        if (cancelled || mapRef.current) return;

        const mapboxMap = new mapboxgl.Map({
          container: containerRef.current,
          accessToken: MAPBOX_TOKEN,
          style: "mapbox://styles/mapbox/light-v11",
          center: [DEFAULT_CENTER.lng, DEFAULT_CENTER.lat],
          zoom: 7,
          attributionControl: false,
          ...(language ? { language: language.split("-")[0] } : {}),
        });

        mapboxMap.zoomIn = (options, eventData) => mapboxMap.zoomTo(mapboxMap.getZoom() + ZOOM_BUTTON_STEP, options, eventData);
        mapboxMap.zoomOut = (options, eventData) => mapboxMap.zoomTo(mapboxMap.getZoom() - ZOOM_BUTTON_STEP, options, eventData);
        mapboxMap.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");
        mapRef.current = mapboxMap;

        if (mapboxMap.loaded()) {
          setMapReady(true);
        } else {
          mapboxMap.on("load", () => {
            if (!cancelled) setMapReady(true);
          });
        }
      } catch (err) {
        console.error("Map init error:", err);
        if (!cancelled) setError(err.message);
      }
    };

    init();

    return () => {
      cancelled = true;
      markersRef.current.forEach((m) => safely(() => m.remove()));
      markersRef.current = [];
      if (mapRef.current) {
        const map = mapRef.current;
        const origRemove = map.remove.bind(map);
        map.remove = () => {
          origRemove();
          if (typeof map.errorCb !== "function") map.errorCb = () => {};
        };
        safely(() => map.remove());
        mapRef.current = null;
      }
      setMapReady(false);
    };
  }, []);

  useEffect(() => {
    if (!mapReady || !containerRef.current || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => safely(() => mapRef.current?.resize()));
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [mapReady]);

  const createMarkerElement = useCallback((cluster, withLabel) => {
    const isSingle = cluster.items.length === 1;

    const el = document.createElement("div");
    Object.assign(el.style, {
      display: "flex",
      alignItems: "center",
      gap: "6px",
      maxWidth: "170px",
      padding: withLabel ? "4px 10px 4px 4px" : "0",
      borderRadius: "999px",
      background: withLabel ? "hsl(0, 0%, 100%)" : "transparent",
      boxShadow: withLabel ? "0 4px 12px hsla(178, 93%, 10%, 0.08)" : "none",
      border: withLabel ? "1px solid hsla(160, 10%, 90%, 0.6)" : "none",
      cursor: "pointer",
      fontSize: "12px",
      lineHeight: "1",
      color: "hsl(178, 93%, 10%)",
      fontFamily: "Segoe UI Bold, sans-serif, system-ui",
      whiteSpace: "nowrap",
    });

    const dot = document.createElement("div");
    Object.assign(dot.style, {
      width: "18px",
      height: "18px",
      borderRadius: "50%",
      background: "hsl(178, 93%, 10%)",
      flexShrink: "0",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "hsl(0, 0%, 100%)",
      fontSize: "10px",
      fontFamily: "Segoe UI Bold, sans-serif, system-ui",
      boxShadow: withLabel ? "none" : "0 4px 12px hsla(178, 93%, 10%, 0.08)",
    });
    if (!isSingle && !withLabel) dot.textContent = String(cluster.items.length);
    el.appendChild(dot);

    if (withLabel && getLabelRef.current) {
      const label = document.createElement("span");
      Object.assign(label.style, { overflow: "hidden", textOverflow: "ellipsis" });
      label.textContent = getLabelRef.current(cluster.items);
      el.appendChild(label);
    }

    return el;
  }, []);

  const activateCluster = useCallback((cluster) => {
    const map = mapRef.current;
    if (!map) return;

    if (cluster.members.length > 1) {
      const lats = cluster.members.map((m) => m.lat);
      const lngs = cluster.members.map((m) => m.lng);
      map.fitBounds(
        [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
        { padding: 60, maxZoom: 14, duration: 600 },
      );
      return;
    }

    setSheetGroup(cluster);
  }, []);

  const buildClusters = useCallback((groups, map, withLabel) => {
    const toCluster = (members) => {
      const items = [];
      members.forEach((m) => m.items.forEach((item) => {
        if (!items.some((x) => x.id === item.id)) items.push(item);
      }));
      const lat = members.reduce((sum, m) => sum + m.lat, 0) / members.length;
      const lng = members.reduce((sum, m) => sum + m.lng, 0) / members.length;
      const width = withLabel
        ? Math.min(LABEL_MAX_WIDTH_PX, 40 + labelTextWidth(String(getLabelRef.current(items))))
        : 0;
      return { key: members.map((m) => m.key).join("|"), lat, lng, items, members, width, point: map.project([lng, lat]) };
    };

    const overlaps = (a, b) => {
      const dx = Math.abs(a.point.x - b.point.x);
      const dy = Math.abs(a.point.y - b.point.y);
      if (!withLabel) return Math.hypot(dx, dy) <= CLUSTER_RADIUS_PX;
      return dx < (a.width + b.width) / 2 + LABEL_GAP_PX && dy < LABEL_HEIGHT_PX + LABEL_GAP_PX;
    };

    const singles = groups.map((group) => toCluster([group]));
    const claimed = new Array(singles.length).fill(false);
    let clusters = [];
    singles.forEach((anchor, i) => {
      if (claimed[i]) return;
      claimed[i] = true;
      const members = [anchor.members[0]];
      for (let j = i + 1; j < singles.length; j += 1) {
        if (!claimed[j] && overlaps(anchor, singles[j])) {
          claimed[j] = true;
          members.push(singles[j].members[0]);
        }
      }
      clusters.push(members.length === 1 ? anchor : toCluster(members));
    });

    let merged = true;
    while (merged) {
      merged = false;
      for (let i = 0; i < clusters.length && !merged; i += 1) {
        for (let j = i + 1; j < clusters.length; j += 1) {
          if (overlaps(clusters[i], clusters[j])) {
            const combined = toCluster([...clusters[i].members, ...clusters[j].members]);
            clusters = clusters.filter((_, k) => k !== i && k !== j).concat(combined);
            merged = true;
            break;
          }
        }
      }
    }

    return clusters;
  }, []);

  const renderMarkers = useCallback(async (groups, { force = false } = {}) => {
    const map = mapRef.current;
    if (!map) return;

    let clusters = buildClusters(groups, map, false);
    const withLabel = Boolean(getLabelRef.current) && clusters.length <= labelLimitRef.current;
    if (withLabel) clusters = buildClusters(groups, map, true);

    const signature = clusters.map((c) => c.key).join("~");
    if (!force && signature === clusterSignatureRef.current) return;
    clusterSignatureRef.current = signature;

    const mapboxgl = (await import("mapbox-gl")).default;
    if (!mapRef.current) return;

    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    renderedRef.current = clusters;

    clusters.forEach((cluster) => {
      const el = createMarkerElement(cluster, withLabel);

      el.addEventListener("click", (e) => {
        e.stopPropagation();
        activateCluster(cluster);
      });

      const marker = new mapboxgl.Marker({ element: el, anchor: "center" })
        .setLngLat([cluster.lng, cluster.lat])
        .addTo(map);

      markersRef.current.push(marker);
    });
  }, [buildClusters, createMarkerElement, activateCluster]);

  useEffect(() => {
    if (!mapReady) return undefined;
    const map = mapRef.current;
    if (!map) return undefined;

    let frame = null;
    const rerender = () => {
      if (frame !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        renderMarkers(groupsRef.current);
      });
    };

    map.on("move", rerender);
    map.on("zoom", rerender);
    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      safely(() => {
        map.off("move", rerender);
        map.off("zoom", rerender);
      });
    };
  }, [mapReady, renderMarkers]);

  const fitGroups = useCallback(async (groups, isCancelled = () => false) => {
    if (!mapRef.current || groups.length === 0) return;
    if (groups.length === 1) {
      mapRef.current.flyTo({ center: [groups[0].lng, groups[0].lat], zoom: 10, duration: 800 });
      return;
    }
    const mapboxgl = (await import("mapbox-gl")).default;
    if (isCancelled() || !mapRef.current) return;
    const bounds = new mapboxgl.LngLatBounds();
    groups.forEach((g) => bounds.extend([g.lng, g.lat]));
    mapRef.current.fitBounds(bounds, { padding: 60, maxZoom: 12, duration: 800 });
  }, []);

  useEffect(() => {
    if (!mapReady || !mapRef.current) return undefined;
    let cancelled = false;

    const groups = groupPoints(points);
    groupsRef.current = groups;
    setSheetGroup(null);
    renderMarkers(groups, { force: true });
    fitGroups(groups, () => cancelled);

    return () => { cancelled = true; };
  }, [mapReady, points, renderMarkers, fitGroups]);

  useEffect(() => {
    if (!focus || focus.lat == null || focus.lng == null || !mapReady || !mapRef.current) return;
    mapRef.current.flyTo({
      center: [Number(focus.lng), Number(focus.lat)],
      zoom: Math.max(mapRef.current.getZoom(), 10),
      duration: 800,
    });
  }, [focus?.lat, focus?.lng, mapReady]);

  useEffect(() => {
    if (!isTouchLayout || !mapReady) return undefined;
    const map = mapRef.current;
    const container = containerRef.current;
    if (!map || !container) return undefined;

    const HIT_RADIUS_PX = 30;

    const onTap = (e) => {
      if (e.target?.closest?.(".mapboxgl-ctrl")) return;
      const clusters = renderedRef.current;
      if (!clusters.length) return;

      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      let closest = null;
      let closestDist = Infinity;
      clusters.forEach((cluster) => {
        const point = map.project([cluster.lng, cluster.lat]);
        const dist = Math.hypot(point.x - x, point.y - y);
        if (dist < closestDist) {
          closestDist = dist;
          closest = cluster;
        }
      });

      if (!closest || closestDist > HIT_RADIUS_PX) return;

      e.stopPropagation();
      activateCluster(closest);
    };

    container.addEventListener("click", onTap, true);
    return () => container.removeEventListener("click", onTap, true);
  }, [isTouchLayout, mapReady, activateCluster]);

  useEffect(() => {
    if (isTouchLayout || !mapReady) return undefined;

    const map = mapRef.current;
    if (!map || !sheetGroup) return undefined;

    let cancelled = false;
    let popup = null;
    const group = sheetGroup;
    const onClose = () => setSheetGroup((current) => (current === group ? null : current));

    const open = async () => {
      const mapboxgl = (await import("mapbox-gl")).default;
      if (cancelled || !mapRef.current) return;

      const node = document.createElement("div");

      popup = new mapboxgl.Popup({
        offset: 18,
        closeButton: true,
        closeOnClick: true,
        maxWidth: "280px",
      })
        .setLngLat([sheetGroup.lng, sheetGroup.lat])
        .setDOMContent(node)
        .addTo(mapRef.current);

      popup.on("close", onClose);

      popupRef.current = popup;
      setPopupContainer(node);
    };

    open();

    return () => {
      cancelled = true;
      popupRef.current = null;
      setPopupContainer(null);
      safely(() => popup?.off("close", onClose));
      safely(() => popup?.remove());
    };
  }, [isTouchLayout, mapReady, sheetGroup]);

  useEffect(() => {
    if (!popupContainer || !sheetGroup) return undefined;
    const frame = requestAnimationFrame(() => {
      const popup = popupRef.current;
      const map = mapRef.current;
      if (!popup || !map || !containerRef.current) return;

      popup.setLngLat([sheetGroup.lng, sheetGroup.lat]);

      const box = popup.getElement().getBoundingClientRect();
      const frameBox = containerRef.current.getBoundingClientRect();
      const margin = 12;
      let dx = 0;
      let dy = 0;
      if (box.top < frameBox.top + margin) dy = box.top - frameBox.top - margin;
      else if (box.bottom > frameBox.bottom - margin) dy = box.bottom - frameBox.bottom + margin;
      if (box.left < frameBox.left + margin) dx = box.left - frameBox.left - margin;
      else if (box.right > frameBox.right - margin) dx = box.right - frameBox.right + margin;
      if (dx || dy) map.panBy([dx, dy], { duration: 300 });
    });
    return () => cancelAnimationFrame(frame);
  }, [popupContainer, sheetGroup]);

  if (error || !MAPBOX_TOKEN) {
    return (
      <Box sx={{
        width: "100%", height: "100%",
        display: "flex", alignItems: "center", justifyContent: "center",
        flexDirection: "column", gap: "8px",
      }}>
        <AlertTriangle size={28} color="hsla(178, 10%, 45%, 0.4)" />
        <Typography sx={{ fontSize: "11px", color: "hsl(178, 10%, 45%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
          {errorMessage || error}
        </Typography>
      </Box>
    );
  }

  const close = () => setSheetGroup(null);
  const closeAndExitFullscreen = () => {
    setSheetGroup(null);
    exitFullscreen();
  };
  const rows = sheetGroup && renderItems
    ? renderItems({ items: sheetGroup.items, close: closeAndExitFullscreen })
    : null;

  return (
    <Box ref={wrapperRef} sx={{ position: "relative", width: "100%", height: "100%", bgcolor: isFullscreen ? "hsl(0, 0%, 100%)" : "transparent" }}>
      <Box
        ref={containerRef}
        sx={{
          width: "100%",
          height: "100%",
          borderRadius: "16px",
          overflow: "hidden",
          "& .mapboxgl-canvas": { borderRadius: "16px" },
          "& .mapboxgl-ctrl-logo": { display: "none !important" },
          "& .mapboxgl-popup-content": {
            borderRadius: "12px",
            padding: "8px 4px 8px 8px",
            boxShadow: "0 2px 12px hsla(178, 93%, 10%, 0.16)",
          },
          "& .mapboxgl-popup-close-button": {
            top: "6px",
            right: "14px",
            width: "20px",
            height: "20px",
            padding: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 0,
            backgroundColor: "transparent",
            color: "hsl(178, 10%, 45%)",
            fontSize: "18px",
            lineHeight: 1,
            fontFamily: "Segoe UI Regular, sans-serif, system-ui",
          },
          "& .mapboxgl-popup-close-button:hover": { backgroundColor: "transparent" },
        }}
      />

      {mapReady && canFullscreen && (
        <Box
          component="button"
          type="button"
          title={isFullscreen ? exitFullscreenLabel : fullscreenLabel}
          aria-label={isFullscreen ? exitFullscreenLabel : fullscreenLabel}
          aria-pressed={isFullscreen}
          onClick={toggleFullscreen}
          sx={{
            position: "absolute", bottom: "10px", right: "10px",
            width: "29px", height: "29px", p: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            border: "none", borderRadius: "4px", cursor: "pointer",
            bgcolor: "hsl(0, 0%, 100%)", color: "hsl(178, 93%, 10%)",
            boxShadow: "0 0 0 2px hsla(0, 0%, 0%, 0.1)",
            "&:hover": { bgcolor: "hsl(0, 0%, 95%)" },
          }}
        >
          {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
        </Box>
      )}

      {(loading || !mapReady) && (
        <Box sx={{
          position: "absolute", top: "16px", left: "50%", transform: "translateX(-50%)",
          display: "flex", alignItems: "center", gap: "8px",
          bgcolor: "hsl(0, 0%, 100%)", borderRadius: "999px", px: "14px", py: "6px",
          boxShadow: "0 4px 12px hsla(178, 93%, 10%, 0.08)", pointerEvents: "none",
        }}>
          <CircularProgress size={12} thickness={5} sx={{ color: "hsl(178, 93%, 10%)" }} />
          {loadingMessage && (
            <Typography sx={{ fontSize: "12px", color: "hsl(178, 10%, 45%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
              {loadingMessage}
            </Typography>
          )}
        </Box>
      )}

      {mapReady && !loading && points.length === 0 && emptyMessage && (
        <Box sx={{
          position: "absolute", top: "16px", left: "50%", transform: "translateX(-50%)",
          bgcolor: "hsl(0, 0%, 100%)", borderRadius: "999px", px: "14px", py: "6px",
          boxShadow: "0 4px 12px hsla(178, 93%, 10%, 0.08)", pointerEvents: "none",
        }}>
          <Typography sx={{ fontSize: "12px", color: "hsl(178, 10%, 45%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
            {emptyMessage}
          </Typography>
        </Box>
      )}

      {!isTouchLayout && popupContainer && createPortal(
        <Box sx={{
          maxHeight: "240px", overflowY: "auto", minWidth: "200px", pr: "4px",
          "& > :first-of-type": { pr: "30px" },
          scrollbarWidth: "thin", scrollbarColor: "hsla(178, 10%, 45%, 0.3) transparent",
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-thumb": { backgroundColor: "hsla(178, 10%, 45%, 0.3)", borderRadius: "999px" },
        }}>
          {rows}
        </Box>,
        popupContainer,
      )}

      {isTouchLayout && (
        <Drawer
          anchor="bottom"
          container={isFullscreen ? wrapperRef.current : undefined}
          open={Boolean(sheetGroup)}
          onClose={close}
          PaperProps={{
            sx: {
              borderTopLeftRadius: "16px",
              borderTopRightRadius: "16px",
              maxHeight: "60vh",
              p: "8px 12px 20px",
            },
          }}
        >
          <Box sx={{
            width: "36px", height: "4px", borderRadius: "2px",
            bgcolor: "hsla(178, 10%, 45%, 0.3)", margin: "0 auto 14px",
          }} />
          {rows}
        </Drawer>
      )}
    </Box>
  );
};

export default GeoMap;
